import { CarFront } from 'lucide-react';
import type { DispatchBooking, SubjectBooking } from '../../../api/types';
import { useTimeFormat } from '../../../lib/tenantTime';
import { bookingRef, bookingTone, pickupDay, pickupLabel } from '../bookingFormat';
import { JobLink } from './JobLink';
import { subjectNamed } from './lastReservation';

/**
 * A contact's last reservation as a chip on a conversation row: the reference
 * and the pickup day, coloured by where the booking stands (the contact panel's
 * palette). The rest — status, time, route, car — is a hover away, since a row
 * has room for one glance. Both lists draw it, so a booking reads the same in
 * the mail layout and the three-pane one.
 *
 * `base` names the chip and, suffixed, its parts — `-face`, `-ref`, `-day` — so
 * each list sizes it to its own row while the content stays one thing.
 *
 * Pressing it opens the booking (JobLink) — except `inRow`, for a row that is
 * itself ONE button (ConversationList): a control inside a button is invalid
 * markup that keyboard users land on twice, so there the chip is text and the
 * row presses it by delegation. `openable` says the row will, which is what
 * gives the chip its hover ring.
 *
 * `named` is the reference a mail subject carried, when the chip shows the
 * reservation that subject names rather than the sender's last one — the
 * hover says which, since the face is the same either way.
 */
export function ReservationChip({
  booking,
  base,
  inRow = false,
  openable = false,
  named = null,
}: {
  booking: DispatchBooking;
  base: string;
  inRow?: boolean;
  openable?: boolean;
  named?: Pick<SubjectBooking, 'matchedOn' | 'reference'> | null;
}) {
  const { zone, formats } = useTimeFormat();
  const tone = bookingTone(booking.status.state);
  // The ride's own zone, as everywhere a pickup is shown — see bookingFormat.
  const rideZone = booking.timeZone ?? zone;
  const day = pickupDay(booking.pickupAt, rideZone, formats);
  const route = [booking.pickup, booking.dropoff].filter(Boolean).join(' → ');
  const title = [
    `${named ? 'In the subject' : 'Last reservation'}: ${bookingRef(booking)} · ${booking.status.label}`,
    // The booker's own number when that is what the subject quoted — the face
    // only ever carries ours.
    named?.matchedOn === 'theirs' ? `Matched on ${subjectNamed(named)}` : null,
    `Pickup ${pickupLabel(booking.pickupAt, rideZone, formats)}`,
    route,
    booking.vehicle,
  ]
    .filter(Boolean)
    .join('\n');
  const face = (
    <span className={`${base}-face`} style={{ background: tone.bg, color: tone.fg }}>
      <CarFront size={inRow ? 10 : 11} />
      <span className={`${base}-ref`}>{bookingRef(booking)}</span>
      {day && <span className={`${base}-day`}>{day}</span>}
    </span>
  );
  if (inRow) {
    return (
      <span className={`${base}${openable ? ' is-openable' : ''}`} title={title}>
        {face}
      </span>
    );
  }
  return (
    <JobLink kind={booking.kind} id={booking.id} className={base} title={title}>
      {face}
    </JobLink>
  );
}
